/**
 * hex-field.js
 * Decorative animated hexagon background.
 *
 * Generates a field of randomly positioned, randomly sized hexagons
 * (outline + faint fill variants) into any `.hex-field` container and
 * animates them on scroll:
 *  - Parallax: each item gets a random `data-speed` registered with
 *    ScrollSmoother (native effects) so they drift at different rates.
 *  - Scale / opacity: a scrubbed ScrollTrigger timeline gently scales the
 *    inner shape up/down and drifts opacity as the section scrolls past.
 *
 * Parallax (transform) is applied to the OUTER item wrapper while
 * scale/opacity is applied to the INNER svg, so ScrollSmoother and GSAP
 * never fight over the same transform matrix.
 *
 * Markup (you add this where you want the background):
 *   <div class="hex-field" data-hex-count="20" aria-hidden="true"></div>
 *
 * Optional data attributes:
 *   data-hex-count        Number of hexagons (default 20)
 *   data-hex-fill         Fraction rendered as faint fills, 0..1 (default 0.38)
 *   data-hex-min          Minimum hexagon width in px (default 24)
 *   data-hex-max          Maximum hexagon width in px (default 95)
 *   data-hex-opacity-min  Lowest opacity reached during the scroll drift (default 0.45)
 *   data-hex-opacity-max  Highest opacity reached during the scroll drift (default 1)
 *   data-hex-speed-min    Slowest parallax speed (default 0.8)
 *   data-hex-speed-max    Fastest parallax speed (default 1.3)
 *
 * Depends on: window.gsap, window.ScrollTrigger (loaded by theme-core).
 * ScrollSmoother is optional — without it (mobile/Safari) hexagons render
 * statically and only the scale/opacity scroll effect runs.
 *
 * @package shi
 */

const HEX_STROKE = '#4dc6c1';
const HEX_FILL = '#99dedb';

// Regular pointy-top hexagon in a normalised viewBox (width 100, height ~115.47).
const HEX_VIEWBOX = '0 0 100 115.47';
const HEX_PATH = 'M50 0 L100 28.87 L100 86.6 L50 115.47 L0 86.6 L0 28.87 Z';

// Default configuration. Every value here can be overridden per field via the
// matching `data-hex-*` attribute (see initHexFields docblock).
const DEFAULTS = {
  count: 28,        // number of hexagons
  fillRatio: 0.38,  // fraction rendered as faint fills (rest are outlines)
  minSize: 32,      // smallest hexagon width in px
  maxSize: 120,      // largest hexagon width in px
  opacityMin: 0, // lowest opacity reached during the scroll drift
  opacityMax: 1,    // highest opacity reached during the scroll drift
  speedMin: 0.15,    // slowest parallax speed
  speedMax: 0.4     // fastest parallax speed
};

// Hexagon ratios that mimic the source art (not user-facing).
const SIZE_BIAS = 1.6;        // >1 skews toward smaller hexagons
const HEX_HEIGHT_RATIO = 1.1547; // height / width for a regular hexagon

/**
 * Random float in [min, max).
 */
function rand(min, max) {
  return min + Math.random() * (max - min);
}

/**
 * Clamp a value into the [min, max] range.
 */
function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

/**
 * Build a single hexagon item (wrapper + svg shape).
 *
 * @param {boolean} isFill Render as faint fill (true) or outline (false).
 * @param {number}  width  Hexagon width in px.
 * @returns {HTMLElement} The wrapper element.
 */
function buildHexItem(isFill, width) {
  const item = document.createElement('div');
  item.className = 'hex-field__item' + (isFill ? ' hex-field__item--fill' : ' hex-field__item--outline');

  const svgNS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(svgNS, 'svg');
  svg.setAttribute('class', 'hex-field__shape');
  svg.setAttribute('viewBox', HEX_VIEWBOX);
  svg.setAttribute('width', '100%');
  svg.setAttribute('height', '100%');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');

  const path = document.createElementNS(svgNS, 'path');
  path.setAttribute('d', HEX_PATH);

  if (isFill) {
    path.setAttribute('fill', HEX_FILL);
    path.setAttribute('fill-opacity', '0.2');
    path.setAttribute('stroke', 'none');
  } else {
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', HEX_STROKE);
    path.setAttribute('stroke-width', '1.25');
    path.setAttribute('vector-effect', 'non-scaling-stroke');
  }

  svg.appendChild(path);
  item.appendChild(svg);

  // Width drives size; aspect ratio keeps it a regular hexagon.
  item.style.width = width + 'px';
  item.style.height = (width * HEX_HEIGHT_RATIO).toFixed(2) + 'px';

  return item;
}

/**
 * Populate one `.hex-field` element with hexagons and wire up animations.
 *
 * @param {HTMLElement} field            The container.
 * @param {object|null} smoother         ScrollSmoother instance (or null).
 * @param {boolean}     reduceMotion     Skip all scroll animation if true.
 */
function buildField(field, smoother, reduceMotion) {
  if (field.dataset.hexInit === '1') return;
  field.dataset.hexInit = '1';

  const gsap = window.gsap;
  const ScrollTrigger = window.ScrollTrigger;

  const count = Math.max(1, parseInt(field.dataset.hexCount, 10) || DEFAULTS.count);
  const fillRatio = Math.min(1, Math.max(0, parseFloat(field.dataset.hexFill) || DEFAULTS.fillRatio));
  const minSize = parseFloat(field.dataset.hexMin) || DEFAULTS.minSize;
  const maxSize = parseFloat(field.dataset.hexMax) || DEFAULTS.maxSize;

  const opacityMin = parseFloat(field.dataset.hexOpacityMin);
  const opacityMax = parseFloat(field.dataset.hexOpacityMax);
  const opMin = isNaN(opacityMin) ? DEFAULTS.opacityMin : opacityMin;
  const opMax = isNaN(opacityMax) ? DEFAULTS.opacityMax : opacityMax;
  const opRange = Math.max(0, opMax - opMin);

  const speedMinAttr = parseFloat(field.dataset.hexSpeedMin);
  const speedMaxAttr = parseFloat(field.dataset.hexSpeedMax);
  const speedMin = isNaN(speedMinAttr) ? DEFAULTS.speedMin : speedMinAttr;
  const speedMax = isNaN(speedMaxAttr) ? DEFAULTS.speedMax : speedMaxAttr;

  const items = [];

  // Jittered-grid placement: split the field into cells sized to the count and
  // aspect ratio, then drop one hexagon per cell with a random offset inside
  // it. This spreads hexagons across the whole area instead of clustering,
  // while the per-cell jitter keeps the layout looking organic.
  const fieldW = field.offsetWidth || 600;
  const fieldH = field.offsetHeight || fieldW * 1.7;
  const aspect = fieldW / fieldH;
  const cols = Math.max(1, Math.round(Math.sqrt(count * aspect)));
  const rows = Math.max(1, Math.ceil(count / cols));

  // One entry per cell, shuffled so the chosen cells are evenly distributed.
  const cells = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      cells.push([c, r]);
    }
  }

  for (let i = cells.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = cells[i];
    cells[i] = cells[j];
    cells[j] = tmp;
  }

  const cellW = 100 / cols;
  const cellH = 100 / rows;

  for (let i = 0; i < count; i++) {
    const isFill = Math.random() < fillRatio;
    // Bias toward smaller hexagons so a few large ones stand out (mimics the source art).
    const sizeT = Math.pow(Math.random(), SIZE_BIAS);
    const width = rand(minSize, maxSize) * (0.6 + sizeT * 0.4);

    const item = buildHexItem(isFill, width);

    // Sizes relative to the field, so hexagons stay fully inside it.
    const widthPct = (width / fieldW) * 100 || 10;
    const heightPct = (width * HEX_HEIGHT_RATIO / fieldH) * 100 || 12;

    // Position inside this hexagon's cell, jittered within the inner 80%.
    const [cx, cy] = cells[i];
    const leftPct = (cx + rand(0.1, 0.9)) * cellW - widthPct / 2;
    const topPct = (cy + rand(0.1, 0.9)) * cellH - heightPct / 2;

    item.style.left = clamp(leftPct, 0, Math.max(0, 100 - widthPct)).toFixed(2) + '%';
    item.style.top = clamp(topPct, 0, Math.max(0, 100 - heightPct)).toFixed(2) + '%';

    field.appendChild(item);
    items.push(item);
  }

  if (reduceMotion || !gsap) return;

  // 1) Parallax — different drift speed per item via ScrollSmoother effects.
  if (smoother && typeof smoother.effects === 'function') {
    items.forEach((item) => {
      const speed = rand(speedMin, speedMax);
      item.setAttribute('data-speed', speed.toFixed(3));
      smoother.effects(item, { speed: speed });
    });
  }

  // 2) Subtle up/down scale + opacity drift, scrubbed to scroll.
  if (ScrollTrigger) {
    items.forEach((item) => {
      const shape = item.firstElementChild;
      if (!shape) return;

      const s1 = rand(0.82, 0.92);
      const s2 = rand(1.05, 1.18);
      const s3 = rand(0.85, 0.95);

      // Per-hexagon show/hide window: stay hidden, fade in at a random scroll
      // point, hold, then fade out. Randomised timing desynchronises every
      // hexagon so they twinkle independently rather than drifting in unison.
      const fadeIn = rand(0.08, 0.2);
      const hold = rand(0.05, 0.3);
      const fadeOut = rand(0.08, 0.2);
      const windowLen = fadeIn + hold + fadeOut;
      const appearAt = rand(0, Math.max(0, 1 - windowLen));
      const hiddenOp = opMin;
      const peakOp = rand(opMin + opRange * 0.6, opMax);

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: field,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true
        }
      });

      // Scale drift across the full scroll range (independent of opacity).
      tl.fromTo(
        shape,
        { scale: s1, transformOrigin: '50% 50%' },
        { scale: s2, ease: 'none', duration: 0.5 },
        0
      ).to(shape, { scale: s3, ease: 'none', duration: 0.5 }, 0.5);

      // Opacity show/hide placed at an absolute, randomised position.
      tl.set(shape, { opacity: hiddenOp }, 0)
        .to(shape, { opacity: peakOp, ease: 'none', duration: fadeIn }, appearAt)
        .to(shape, { opacity: hiddenOp, ease: 'none', duration: fadeOut }, appearAt + fadeIn + hold);
    });

    if (typeof window.requestScrollTriggerRefresh === 'function') {
      window.requestScrollTriggerRefresh();
    } else if (typeof ScrollTrigger.refresh === 'function') {
      ScrollTrigger.refresh();
    }
  }
}

/**
 * Initialise all `.hex-field` containers on the page.
 *
 * @param {ParentNode} [root=document] Scope to search within.
 */
export function initHexFields(root = document) {
  const fields = root.querySelectorAll('.hex-field');
  if (!fields.length) return;

  const reduceMotion =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const smoother = window.ScrollSmoother && typeof window.ScrollSmoother.get === 'function'
    ? window.ScrollSmoother.get()
    : null;

  fields.forEach((field) => buildField(field, smoother, reduceMotion));
}
